/**
 * Where the user is, as the bot is told it. The URL is the truth: every Ydistri URL opens exactly
 * where it was copied from, so its identifiers - a calculation, a filtered SKU in the query string -
 * are what let the bot know which "this" a question means. The route pattern is the stable name for
 * the kind of screen, and it comes from the router's own matching rather than from page titles the
 * app translates.
 */

/** One matched route, as `matchRoutes` reports it. Only the parts this file reads. */
export interface YbotRouteMatch {
  route: { path?: string };
  params: Record<string, string | undefined>;
}

export interface YbotScreen {
  url: string;
  route: string;
  params: Record<string, string>;
}

/**
 * Joins the matched routes' own patterns into one. Deliberately not "substitute the param values
 * back into the pathname": a merged params object says which names matched which values, not which
 * segment each name came from, so two params with the same value - `/aso/:asoRequestId/:asoScenarioId`
 * with both `123` - cannot be told apart that way.
 */
export const screenFromMatches = (
  matches: readonly YbotRouteMatch[] | null | undefined,
  location: { pathname: string; search: string },
): YbotScreen | undefined => {
  if (!matches || matches.length === 0) {
    return undefined;
  }

  const segments = matches
    .flatMap(match => (match.route.path ?? '').split('/'))
    .filter(segment => segment.length > 0);

  // The last match carries the whole branch's params; react-router merges them as it descends.
  const matched = matches[matches.length - 1].params;
  const params: Record<string, string> = {};
  for (const [name, value] of Object.entries(matched)) {
    if (value !== undefined && value !== '') {
      params[name] = value;
    }
  }

  return {
    url: `${location.pathname}${location.search}`,
    route: `/${segments.join('/')}`,
    params,
  };
};

/** What the server stored with a user message: its project, its database, and the screen. */
export interface YbotWindowContext {
  project?: string;
  database?: string;
  screen?: { url?: string; route?: string };
}

/**
 * Reads back what the server stored on a message. Guarded rather than trusted: the column also
 * holds rows written before it had this shape, and a half-written value must not take the dock
 * down.
 */
export const parseWindowContext = (
  contextJson: string | null | undefined,
): YbotWindowContext | undefined => {
  if (!contextJson) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(contextJson);
  } catch {
    return undefined;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- narrowed to a plain object above; every field is read defensively below
  const record = parsed as Record<string, unknown>;
  const screen =
    typeof record.screen === 'object' && record.screen !== null && !Array.isArray(record.screen)
      ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- same narrowing, one level down
        (record.screen as Record<string, unknown>)
      : undefined;

  const context: YbotWindowContext = {
    project: typeof record.project === 'string' ? record.project : undefined,
    database: typeof record.database === 'string' ? record.database : undefined,
    screen:
      screen === undefined
        ? undefined
        : {
            url: typeof screen.url === 'string' ? screen.url : undefined,
            route: typeof screen.route === 'string' ? screen.route : undefined,
          },
  };

  // Nothing worth showing is the same as nothing stored.
  return context.project === undefined && context.screen?.url === undefined ? undefined : context;
};
